import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import Button from '@material-ui/core/Button';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

export interface ConfirmDialogProps {
    open:          boolean;
    title?:        string;
    message?:      React.ReactNode;
    confirmLabel?: string;
    cancelLabel?:  string;
    onConfirm:     () => void;
    onCancel:      () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        content: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            paddingTop: theme.spacing(0.5),
        },
        icon: {
            color: theme.palette.warning.main,
            fontSize: 28,
            marginTop: 2,
            flexShrink: 0,
        },
        message: {
            lineHeight: 1.6,
        },
    })
);

/**
 * Generic confirm/discard prompt. Reusable anywhere a user action needs a
 * "are you sure?" gate — e.g. closing a dirty form, deleting a record, etc.
 * Uses Material UI's default Dialog/Button styling throughout.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title = 'Are you sure?',
    message = 'This action cannot be undone.',
    confirmLabel = 'Yes',
    cancelLabel = 'Cancel',
    onConfirm,
    onCancel,
}) => {
    const classes = useStyles();

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <div className={classes.content}>
                    <WarningRoundedIcon className={classes.icon} />
                    <DialogContentText className={classes.message}>
                        {message}
                    </DialogContentText>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>
                    {cancelLabel}
                </Button>
                <Button onClick={onConfirm} color="primary" variant="contained" autoFocus>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;